import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../context/AuthContext";
import { toast } from "sonner";
import { readPref, writePref } from "../lib/consent";

const LOC_PREF = "customer_location_v1";

export function readSavedLocation() {
  const o = readPref(LOC_PREF, null);
  if (o && typeof o.latitude === "number" && typeof o.longitude === "number") return o;
  return null;
}

export function saveLocation(loc) {
  // No-ops silently when the user hasn't consented to functional cookies.
  if (!loc) writePref(LOC_PREF, null);
  else writePref(LOC_PREF, loc);
}

/**
 * Reusable location picker.
 *  - "Use My Location" → browser geolocation (one-time, with permission)
 *  - "Enter Location Manually" → reveals city/ZIP/address input, geocoded server-side
 * Persists the chosen location in localStorage so subsequent searches stay scoped.
 * The customer's exact coords are NEVER sent to any business — only used client-side to filter
 * the /api/services list. (Mobile-service bookings disclose location separately at booking time.)
 */
export default function LocationPicker({ value, onChange, compact = false }) {
  const [mode, setMode] = useState(value ? value.source : null); // 'gps' | 'manual'
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(value?.label || "");
  const [openManual, setOpenManual] = useState(false);

  useEffect(() => {
    if (value) {
      setMode(value.source);
      setManual(value.label || "");
    }
  }, [value]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location. Try entering it manually.");
      setOpenManual(true);
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          label: "Current location",
          source: "gps",
        };
        saveLocation(loc);
        onChange?.(loc);
        setMode("gps");
        setBusy(false);
        toast.success("Using your current location");
      },
      (err) => {
        setBusy(false);
        if (err.code === 1) {
          toast.message("Location blocked — enter it manually instead.");
          setOpenManual(true);
        } else {
          toast.error("Couldn't get your location. Try entering it manually.");
          setOpenManual(true);
        }
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8000 }
    );
  };

  const submitManual = async (e) => {
    e?.preventDefault?.();
    if (!manual || manual.trim().length < 2) {
      toast.error("Enter a city, ZIP, or address");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/locations/geocode`, { params: { q: manual.trim() } });
      const loc = {
        latitude: r.data.latitude,
        longitude: r.data.longitude,
        label: r.data.display_name || manual.trim(),
        city: r.data.city,
        state: r.data.state,
        zip_code: r.data.zip_code,
        source: "manual",
      };
      saveLocation(loc);
      onChange?.(loc);
      setMode("manual");
      setBusy(false);
      setOpenManual(false);
    } catch (err) {
      setBusy(false);
      toast.error(err?.response?.data?.detail || "Couldn't find that place");
    }
  };

  const clear = () => {
    saveLocation(null);
    onChange?.(null);
    setMode(null);
    setManual("");
  };

  if (value) {
    return (
      <div data-testid="location-picker-set" className={`flex items-center gap-2 ${compact ? "text-sm" : ""}`}>
        <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gf-accent)" }} />
          {value.source === "gps" ? "Near my location" : (value.city || value.label)}
        </span>
        <button data-testid="location-change-btn" onClick={() => { clear(); setOpenManual(true); }} className="text-xs underline" style={{ color: "var(--gf-text-muted)" }}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="location-picker">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="use-my-location-btn"
          onClick={useMyLocation}
          disabled={busy}
          className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
          style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline-block mr-1.5 -mt-0.5">
            <circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          </svg>
          {busy && mode === "gps" ? "Getting location…" : "Use my location"}
        </button>
        <button
          type="button"
          data-testid="enter-location-manually-btn"
          onClick={() => setOpenManual(!openManual)}
          className="rounded-full px-4 py-2 text-sm border"
          style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
        >
          Enter location manually
        </button>
      </div>
      {openManual && (
        <form onSubmit={submitManual} className="flex items-stretch gap-2 mt-2" data-testid="location-manual-form">
          <input
            data-testid="location-manual-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="City, ZIP code, or full address"
            className="flex-1 bg-transparent border rounded-full px-4 py-2 text-sm outline-none focus:border-[var(--gf-accent)]"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            autoFocus
          />
          <button type="submit" disabled={busy} className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            {busy ? "…" : "Set"}
          </button>
        </form>
      )}
      <div className="text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
        We use your location only to show nearby services. Your exact location is never shared with businesses.
      </div>
    </div>
  );
}
